import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../../../component/context/AuthContext';
import * as CONST from '../../../component/config/Constant';
import { toast } from 'react-toastify';
import "../../../assets/css/table.css";
import { IconRotate2 } from '@tabler/icons-react';
import AddQuestionModel from './AddQuestionModel';
import ViewSetModal from './ViewSetModal';
import { encrypt } from '../../quiz/Helper';
import Swal from 'sweetalert2';
import DataTable from "react-data-table-component";
import HeaderSection from "../banner/HeaderSection";
import DivLoader from "../../../component/utilities/Spinner";
import { EditIcon, DeleteIcon, ViewIcon, AddIcon, CopyLinkIcon, RestoreIcon} from '../../../component/utilities/ActionIcons';
import ReactSwitch from "react-switch";
import * as SwitchProps from "../../../component/utilities/ReactSwitchReactSwitchProps";
import Loading from "react-fullscreen-loading";

export default function IsoSetsList() {
  const [authUserData] = useContext(AuthContext);
  const token = authUserData && authUserData[0] ? authUserData[0].token : '';
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // Search state
  const [searchId, setSearchId] = useState('');
  const [searchTitle, setSearchTitle] = useState('');

  // Modal State
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [targetSetId, setTargetSetId] = useState(null);
  const [viewSetId, setViewSetId] = useState(null);

  const [maxPageLimit, setMaxPageLimit] = useState(10);
  const [minPageLimit, setMinPageLimit] = useState(0);
  const pageNumberLimit = 10;

  useEffect(() => {
    fetchSets(currentPage);
  }, [currentPage]);

  async function fetchSets(
    page = 1,
    idSearch = searchId,
    titleSearch = searchTitle,
  ) {
    setLoading(true);
    try {
      const params = { page: page, limit: perPage };
      if (idSearch) params.id = idSearch;
      if (titleSearch) params.title = titleSearch;

      const resp = await axios.get(CONST.ISO_SET_LIST, {
        headers: { Authorization: `Basic ${token}` },
        params: params,
      });

      if (resp.data && resp.data.status === "success") {
        setSets(resp.data.data || []);
        setTotalRows(resp.data.total || (resp.data.data ? resp.data.data.length : 0));
        setCurrentPage(page);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error fetching sets");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to delete this set?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setFullLoading(true);
        const resp = await axios.delete(CONST.ISO_SET_DELETE + id, {
          headers: { Authorization: `Basic ${token}` }
        });

        if (resp.data && resp.data.status === 'success') {
          toast.success('Assignment Inactive');
          fetchSets(1, searchId, searchTitle);
        } else {
          toast.error(resp.data.message || 'Delete failed');
        }
      } catch (err) {
        console.error(err);
        toast.error('Error deleting');
      } finally {
        setFullLoading(false);
      }
    }
  }

  function handleSearch() {
    setCurrentPage(1);
    fetchSets(1, searchId, searchTitle);
  }

  function handleResetSearch() {
    setSearchId('');
    setSearchTitle('');
    setCurrentPage(1);
    fetchSets(1, '', '');
  }

  const handleToggleStatus = async (id, currentStatus, index) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to ${currentStatus ? "deactivate" : "activate"} this assignment?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, change it!'
    });

    if (result.isConfirmed) {
      try {
        setFullLoading(true);
        const resp = await axios.put(CONST.ISO_SET_UPDATE + id, {
          is_active: !currentStatus,
        }, {
          headers: { Authorization: `Basic ${token}` },
        });

        if (resp.data && resp.data.status === 'success') {
          toast.success("Status updated");
          const updatedSets = [...sets];
          updatedSets[index].is_active = !currentStatus;
          setSets(updatedSets);
        } else {
          toast.error(resp.data?.message || "Failed to update status");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error updating status");
      } finally {
        setFullLoading(false);
      }
    }
  };

  const handleRestore = async (id) => {
    const result = await Swal.fire({
      title: "Restore Assignment?",
      text: "Do you want to restore this assignment?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, restore it!",
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    });

    if (result.isConfirmed) {
      try {
        setFullLoading(true);
        const resp = await axios.put(
          CONST.ISO_SET_UPDATE + id,
          { is_active: true },
          { headers: { Authorization: `Basic ${token}` } }
        );

        if (resp.data && resp.data.status === "success") {
          toast.success("Assignment restored successfully");
          fetchSets(currentPage, searchId, searchTitle);
        } else {
          toast.error(resp.data?.message || "Restore failed");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error restoring assignment");
      } finally {
        setFullLoading(false);
      }
    }
  };

  const openAddQuestionForSet = (sid) => {
    setTargetSetId(sid);
    setShowQuestionModal(true);
  };

  const openViewModal = (sid) => {
    setViewSetId(sid);
    setShowViewModal(true);
  };

  const handleCopyLink = (setId, isActive) => {
    if (!isActive) {
      toast.error("This assignment is inactive. You can't copy this link.");
      return;
    }
    const encryptedId = encrypt(setId);
    const shareableLink = `${window.location.origin}/quiz/start/${encryptedId}`;
    navigator.clipboard.writeText(shareableLink)
      .then(() => toast.success("Link copied to clipboard!"))
      .catch(() => toast.error("Failed to copy link"));
  };

  const handleQuestionAdded = () => {
    fetchSets(currentPage, searchId, searchTitle);
    setTargetSetId(null);
  };

  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      width: "8%",
      center: true,
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
      width: "25%",
    },
    {
      name: "Date",
      selector: (row) => row.created_at,
      width: "15%",
      center: true,
      cell: (row) => (
        row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "-"
      ),
    },
    {
      name: "Status",
      width: "10%",
      center: true,
      cell: (row, index) => (
        <ReactSwitch
          onColor={SwitchProps.onColor}
          onHandleColor={SwitchProps.onHandleColor}
          handleDiameter={SwitchProps.handleDiameter}
          uncheckedIcon={SwitchProps.uncheckedIcon}
          checkedIcon={SwitchProps.checkedIcon}
          boxShadow={SwitchProps.boxShadow}
          activeBoxShadow={SwitchProps.activeBoxShadow}
          height={SwitchProps.height}
          width={SwitchProps.width}
          className={SwitchProps.className}
          checked={row.is_active}
          onChange={() => handleToggleStatus(row.id, row.is_active, index)}
        />
      ),
    },
    {
      name: "Questions",
      selector: (row) => row.total_questions,
      width: "12%",
      center: true,
    },
    {
      name: "Submissions",
      width: "12%",
      center: true,
      cell: (row) => (
        <span
          className="fw-bold text-primary"
          style={{ cursor: "pointer" }}
          onClick={() => navigate(CONST.ADMIN_PRE + "iso-submissions?setId=" + encrypt(row.id))}
        >
          {row.total_submissions ?? 0}
        </span>
      ),
    },
    {
      name: "Actions",
      width: "18%",
      center: true,
      cell: (row) => (
        <div className="d-flex gap-1">
          {!row.is_active ? (
            <button className="btn btn-sm" title="Restore" onClick={() => handleRestore(row.id)}>
              <RestoreIcon size={20} className="text-primary" />
            </button>
          ) : (
            <>
              <CopyLinkIcon tooltip="Copy Link" onClick={() => handleCopyLink(row.id, row.is_active)} />
              <ViewIcon tooltip="View Set" onClick={() => openViewModal(row.id)} />
              <AddIcon tooltip="Add Question" onClick={() => openAddQuestionForSet(row.id)} />
              <EditIcon onClick={() => navigate(CONST.ADMIN_PRE + "iso-quiz/sets/edit/" + row.id)} />
              <DeleteIcon onClick={() => handleDelete(row.id)} />
            </>
          )}
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const BootyPagination = ({
    rowsPerPage,
    rowCount,
    onChangePage,
    currentPage,
  }) => {
    const handleBackButtonClick = () => {
      if ((currentPage - 1) % pageNumberLimit === 0) {
        setMaxPageLimit(maxPageLimit - pageNumberLimit);
        setMinPageLimit(minPageLimit - pageNumberLimit);
      }
      onChangePage(currentPage - 1);
    };

    const handleNextButtonClick = () => {
      if (currentPage + 1 > maxPageLimit) {
        setMaxPageLimit(maxPageLimit + pageNumberLimit);
        setMinPageLimit(minPageLimit + pageNumberLimit);
      }
      onChangePage(currentPage + 1);
    };

    const totalPages = Math.ceil(rowCount / rowsPerPage);
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    const nextDisabled = currentPage === totalPages;
    const previousDisabled = currentPage === 1;

    return (
      <div className="card-footer d-flex justify-content-between align-items-center">
        <p className="m-0 text-muted small">
          Showing page {currentPage} of {totalPages}
        </p>
        <ul className="pagination pagination-sm m-0 ms-auto">
          <li className={`page-item ${previousDisabled ? "disabled" : ""}`}>
            <button className="page-link" onClick={handleBackButtonClick} disabled={previousDisabled}>
              «
            </button>
          </li>
          {pages.map((page) => {
            if (page <= maxPageLimit && page > minPageLimit) {
              return (
                <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => onChangePage(page)}>
                    {page}
                  </button>
                </li>
              );
            }
            return null;
          })}
          <li className={`page-item ${nextDisabled ? "disabled" : ""}`}>
            <button className="page-link" onClick={handleNextButtonClick} disabled={nextDisabled}>
              »
            </button>
          </li>
        </ul>
      </div>
    );
  };

  const CustomLoader = () => (
    <div style={{ padding: "24px" }} className="text-center">
      <DivLoader color="primary" />
    </div>
  );

  return (
    <>
      <Loading loading={fullLoading} background="rgba(0, 0, 0, 0.5)" loaderColor="#fff" />
      <HeaderSection
        title="Admin"
        heading="ISO Quiz Assignments"
        addLink={CONST.ADMIN_PRE + "iso-quiz/sets/add"}
        addLinkName="Add Assignment"
        linktype="add"
      />
      <div className="page-body">
        <div className="container-xl">
          <div className="row row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Listing</h3>
                </div>
                <div className="card-body border-bottom py-3">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group mb-3">
                        <label className="form-label">Assignment Title</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Assignment title"
                          value={searchTitle}
                          onChange={(e) => setSearchTitle(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mt-4">
                      <div className="d-flex gap-2 mt-2">
                        <button className="btn btn-primary mr-2" onClick={handleSearch}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
															<path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
															<circle cx="10" cy="10" r="7"></circle>
															<line x1="21" y1="21" x2="15" y2="15"></line>
														</svg>Search</button>&nbsp;
                        <button className="btn btn-outline-primary" onClick={handleResetSearch}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
															<path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
															<line x1="18" y1="6" x2="6" y2="18"></line>
															<line x1="6" y1="6" x2="18" y2="18"></line>
														</svg>Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  columns={columns}
                  data={sets}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  paginationComponent={BootyPagination}
                  progressPending={loading}
                  progressComponent={<CustomLoader />}
                  noDataComponent={<div className="p-4 text-center">No assignments found.</div>}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddQuestionModel
        show={showQuestionModal}
        handleClose={() => setShowQuestionModal(false)}
        onQuestionAdded={handleQuestionAdded}
      />

      <ViewSetModal
        show={showViewModal}
        handleClose={() => setShowViewModal(false)}
        setId={viewSetId}
      />
    </>
  );
}
